import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Smile } from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { FocusHeader } from '../components/FocusHeader';
import { extractPostContent } from '../lib/socialContent';
import { ScheduleSocialPostModal } from '../components/ScheduleSocialPostModal';
import { loadScheduleTargets, type ScheduleTargets } from '../lib/scheduleTargets';
import { SocialImageStrip } from '../components/SocialImageStrip';
import { parseImages, type SocialImage } from '../lib/socialImages';
import {
  removeSocialImageFile,
  saveSocialImages,
  uploadSocialImage,
} from '../lib/socialImageStorage';

interface SocialPost {
  id: string;
  platform: string;
  post_content: string;
  image_url: string | null;
  status: string;
  scheduled_at: string | null;
  blog_id: string | null;
  facebook_page_id: string | null;
  linkedin_page_id: string | null;
  images: unknown;
  user_id: string;
}

const EMOJI_LIST = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂',
  '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋',
  '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳',
  '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '😣', '😖', '😫',
  '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳',
  '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭',
  '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧',
  '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢',
  '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '👍', '👎', '👏',
  '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🦿', '🦵',
  '🦶', '👂', '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀',
  '👁️', '👅', '👄', '💋', '🩸', '❤️', '🧡', '💛', '💚', '💙',
  '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗',
  '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️',
  '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋',
  '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓', '🆔', '⚛️',
  '🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔺',
  '🔻', '🔸', '🔹', '🔶', '🔷', '🔳', '🔲', '▪️', '▫️', '◾',
  '◽', '◼️', '◻️', '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '⬛',
  '⬜', '🟫', '🎯', '🎲', '🎮', '🎰', '🎪', '🎭', '🎨', '🎬',
  '🎤', '🎧', '🎼', '🎹', '🥁', '🎷', '🎺', '🎸', '🪕', '🎻',
  '🎲', '♟️', '🎯', '🎳', '🎮', '🎰', '🧩', '🚀', '🛸', '🛰️',
  '💺', '🚁', '🛶', '⛵', '🚤', '🛥️', '🛳️', '⛴️', '🚢', '⚓',
  '⛽', '🚧', '🚦', '🚥', '🚏', '🗺️', '🗿', '🗽', '🗼', '🏰',
  '🏯', '🏟️', '🎡', '🎢', '🎠', '⛲', '⛱️', '🏖️', '🏝️', '🏜️',
  '🌋', '⛰️', '🏔️', '🗻', '🏕️', '⛺', '🏠', '🏡', '🏘️', '🏚️',
  '🏗️', '🏭', '🏢', '🏬', '🏣', '🏤', '🏥', '🏦', '🏨', '🏪',
  '🏫', '🏩', '💒', '🏛️', '⛪', '🕌', '🕍', '🛕', '🕋', '⛩️',
  '💡', '🔦', '🕯️', '🪔', '🧯', '🛢️', '💸', '💵', '💴', '💶',
  '💷', '🪙', '💰', '💳', '💎', '⚖️', '🪜', '🪛', '🪝', '🧰',
  '🧲', '🪓', '⛏️', '⚒️', '🛠️', '🗡️', '⚔️', '🔫', '🪃', '🏹',
  '🛡️', '🪚', '🔧', '🪛', '🔩', '⚙️', '🗜️', '⚗️', '🧪', '🧫',
  '🧬', '🔬', '🔭', '📡', '💉', '🩸', '💊', '🩹', '🩺', '🌡️',
  '🧹', '🪠', '🧺', '🧻', '🚽', '🚰', '🚿', '🛁', '🛀', '🪒',
  '🧴', '🧷', '🧹', '🧺', '🧻', '🪣', '🧼', '🪥', '🧽', '🧯',
  '🔥', '💥', '💫', '💦', '💨', '🌟', '⭐', '🌠', '🌌', '☁️',
  '⛅', '⛈️', '🌤️', '🌥️', '🌦️', '🌧️', '🌨️', '🌩️', '🌪️', '🌫️',
  '🌬️', '🌀', '🌈', '🌂', '☂️', '☔', '⛱️', '⚡', '❄️', '☃️',
  '⛄', '☄️', '🔥', '💧', '🌊', '🎄', '✨', '🎋', '🎍'
];

// Terugval voor de Instagram-preview zolang de cover nog niet (opnieuw)
// gemeten is, zodat het vlak nooit dichtklapt (zie het effect hieronder). Een
// lichte liggende verhouding in plaats van vierkant: covers komen meestal uit
// foto's of uitgelichte blogbeelden, die vaker liggend dan vierkant zijn, dus
// dit voorkomt de opvallendste sprong (vierkant -> brede band) als de echte
// meting straks binnenkomt.
const DEFAULT_COVER_RATIO = '4 / 3';

export default function SocialMediaEdit() {
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();
  const [post, setPost] = useState<SocialPost | null>(null);
  const [content, setContent] = useState('');
  // imageUrl blijft de gearchiveerde cover-waarde bijhouden voor het opslaan;
  // de preview leest 'm niet meer, vandaar de underscore die eslint stil houdt.
  const [_imageUrl, setImageUrl] = useState('');
  const [images, setImages] = useState<SocialImage[]>([]);
  const [imagesBusy, setImagesBusy] = useState(false);
  const [slide, setSlide] = useState(0);
  const [coverRatio, setCoverRatio] = useState<string>(DEFAULT_COVER_RATIO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleCheck, setScheduleCheck] = useState<{
    checking: boolean;
    blockReason: string | null;
    targets: ScheduleTargets | null;
  }>({ checking: false, blockReason: null, targets: null });
  const scheduleCheckSeq = useRef(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    loadPost();
  }, [postId]);

  useEffect(() => {
    // Instagram duwt alle slides in de verhouding van de cover; laat dat zien.
    setSlide((s) => (s < images.length ? s : 0));
    const cover = images[0]?.url;
    if (!cover) {
      setCoverRatio(DEFAULT_COVER_RATIO);
      return;
    }
    // Wisselt de klant snel van cover (slepen, of een beeld vooraan
    // verwijderen), dan kan de tragere onload van een OUDERE meting na de
    // snellere van de nieuwe cover binnenkomen. De opruimfunctie zet deze
    // vlag zodra het effect opnieuw draait (nieuwe cover) of de component
    // wordt verlaten; onload doet dan niets meer, dus alleen de meting van de
    // huidige cover mag de state nog bijwerken.
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setCoverRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
      }
    };
    img.src = cover;
    return () => {
      cancelled = true;
    };
  }, [images]);

  async function loadPost() {
    try {
      const { data, error } = await supabase
        .from('social_media_posts')
        .select('*')
        .eq('id', postId)
        .single();

      if (error) throw error;

      // Self-healing: reeds opgeslagen drafts kunnen nog de ruwe
      // {"content": "...\\n"} blob bevatten — normaliseer bij laden.
      const cleanContent = extractPostContent(data.post_content);
      setPost({ ...data, post_content: cleanContent });
      setContent(cleanContent);
      setImageUrl(data.image_url || '');

      const geladen = parseImages(data.images);
      // Legacy: alleen image_url gevuld → toon dat als eerste tegel.
      setImages(
        geladen.length > 0
          ? geladen
          : data.image_url
            ? [{ url: data.image_url, storage_path: '' }]
            : [],
      );
    } catch (err: any) {
      console.error('Error loading post:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Elke wijziging gaat meteen naar de database. Zo raakt een klant die het
  // scherm verlaat zonder in te plannen zijn uploads niet kwijt.
  // Geeft terug of het opslaan lukte: bij verwijderen mag het bestand pas uit
  // storage weg nadat de database daadwerkelijk niet meer naar dit beeld
  // wijst, anders bestaat het beeld straks nergens meer terwijl de rij er nog
  // naar verwijst.
  async function persistImages(next: SocialImage[]): Promise<boolean> {
    if (!post) return false;
    setImages(next);
    setImageUrl(next[0]?.url ?? '');
    try {
      await saveSocialImages(post.id, next, next[0]?.url ?? null);
      return true;
    } catch (err) {
      console.error('Error saving images:', err);
      setError('De beelden konden niet worden opgeslagen. Probeer het opnieuw.');
      return false;
    }
  }

  async function handleAddImages(files: File[]) {
    if (!post) return;
    setImagesBusy(true);
    const toegevoegd: SocialImage[] = [];
    for (const file of files) {
      try {
        toegevoegd.push(await uploadSocialImage(file, post.user_id, post.id));
      } catch (err) {
        console.error('Error uploading image:', err);
        setError(`We konden ${file.name} niet verwerken. Probeer een JPG-, PNG- of WebP-bestand.`);
      }
    }
    if (toegevoegd.length > 0) await persistImages([...images, ...toegevoegd]);
    setImagesBusy(false);
  }

  async function handleRemoveImage(index: number) {
    if (!post) return;
    const weg = images[index];
    setImagesBusy(true);
    const saved = await persistImages(images.filter((_, i) => i !== index));
    // Alleen opruimen als de database ook echt niet meer naar dit bestand
    // wijst; anders staat de rij nog op een beeld dat al weg is.
    if (saved) {
      await removeSocialImageFile(weg, post.id, post.user_id);
    }
    setImagesBusy(false);
  }

  // Herordenen (slepen of de pijltjes op een tegel) schrijft net als
  // toevoegen en verwijderen de volledige reeks in één keer weg, zonder
  // versiecontrole. Zonder vergrendeling kan de klant tijdens het wegschrijven
  // van deze herordening opnieuw slepen, toevoegen of verwijderen; komt het
  // antwoord van die oudere schrijfactie later binnen dan het nieuwere, dan
  // wint de oudere in de database terwijl het scherm allang de nieuwe
  // volgorde toont. Dezelfde vergrendeling als bij add/remove voorkomt dat:
  // zolang imagesBusy aanstaat kan er geen tweede schrijfactie starten, dus
  // ze kunnen nooit overlappen. Het try/finally garandeert dat de vergrendeling
  // ook vrijkomt als het opslaan faalt.
  async function handleReorderImages(next: SocialImage[]) {
    setImagesBusy(true);
    try {
      await persistImages(next);
    } finally {
      setImagesBusy(false);
    }
  }

  // Stap 1: tekst opslaan, dan meteen de inplan-modal openen.
  async function handlePlan() {
    if (!post) return;

    setSaving(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('social_media_posts')
        .update({
          post_content: content,
        })
        .eq('id', post.id);

      if (error) throw error;

      setPost({ ...post, post_content: content }); // baseline -> geen "niet opgeslagen" meer
      setScheduleModalOpen(true);
      if (post.platform === 'facebook' || post.platform === 'linkedin') {
        const seq = ++scheduleCheckSeq.current;
        setScheduleCheck({ checking: true, blockReason: null, targets: null });
        loadScheduleTargets(post.platform).then((targets) => {
          if (scheduleCheckSeq.current === seq) {
            setScheduleCheck({
              checking: false,
              blockReason: targets.blockReason,
              targets,
            });
          }
        });
      } else {
        setScheduleCheck({ checking: false, blockReason: null, targets: null });
      }
    } catch (err) {
      console.error('Error saving post:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  // Stap 2: keuze uit de modal -> inplannen en terug naar de lijst.
  async function handleScheduleConfirm(scheduledAt: Date, targetPageId?: string | null) {
    if (!post) return;
    try {
      // Gekozen doel op de post snapshotten. LinkedIn persoonlijk = sentinel
      // 'personal' (NULL zou terugvallen op de profiel-default).
      const targetUpdate: Record<string, unknown> = {};
      if (targetPageId !== undefined) {
        if (post.platform === 'facebook' && targetPageId) {
          targetUpdate.facebook_page_id = targetPageId;
        }
        if (post.platform === 'linkedin') {
          targetUpdate.linkedin_page_id = targetPageId ?? 'personal';
        }
      }

      const { error } = await supabase
        .from('social_media_posts')
        // Verse start: oude foutmelding weg en de retry-teller terug naar 0,
        // anders is de eerstvolgende mislukte poging meteen definitief.
        .update({
          scheduled_at: scheduledAt.toISOString(),
          status: 'scheduled',
          error_message: null,
          retry_count: 0,
          ...targetUpdate,
        })
        .eq('id', post.id);

      if (error) throw error;
      navigate('/social-media/posts');
    } catch (err) {
      console.error('Error scheduling post:', err);
      setError('Fout bij inplannen: ' + (err instanceof Error ? err.message : String(err)));
    }
  }

  function insertEmoji(emoji: string) {
    if (!textareaRef) return;

    const start = textareaRef.selectionStart;
    const end = textareaRef.selectionEnd;
    const newContent = content.substring(0, start) + emoji + content.substring(end);

    setContent(newContent);
    setShowEmojiPicker(false);

    setTimeout(() => {
      textareaRef.focus();
      textareaRef.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  }

  function getPlatformIcon() {
    if (!post) return null;

    switch (post.platform) {
      case 'facebook':
        return (
          <div className="w-10 h-10 bg-[#1877F2] rounded-full flex items-center justify-center text-white font-bold">
            f
          </div>
        );
      case 'linkedin':
        return (
          <div className="w-10 h-10 bg-[#0A66C2] rounded-full flex items-center justify-center text-white font-bold">
            in
          </div>
        );
      case 'instagram':
        return (
          <div className="w-10 h-10 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
            ig
          </div>
        );
      default:
        return null;
    }
  }

  const characterLimit = post?.platform === 'x' ? 280 : post?.platform === 'linkedin' ? 1300 : 2000;
  const hasUnsavedChanges = post ? content !== post.post_content : false;
  const characterPercentage = (content.length / characterLimit) * 100;
  const isOverLimit = content.length > characterLimit;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <div className="text-center py-12">
          <p className="text-gray-600">Post niet gevonden</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title={`${post.platform.charAt(0).toUpperCase() + post.platform.slice(1)} post bewerken`}
        onBack={() => navigate('/social-media/posts')}
        customActions={
          <div className="flex items-center gap-3">
            {hasUnsavedChanges && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                <span>Niet opgeslagen</span>
              </div>
            )}
            <button
              onClick={handlePlan}
              disabled={saving || isOverLimit || imagesBusy}
              className="btn-primary text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Bezig…' : 'Plannen'}
            </button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Editor Section */}
          <div className="space-y-4">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Bewerk je post</h2>

                {/* Emoji Picker Button */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors border border-blue-200"
                    title="Emoji toevoegen"
                  >
                    <Smile className="w-5 h-5" />
                    <span className="text-sm font-medium">Emoji</span>
                  </button>

                  {/* Emoji Picker Dropdown */}
                  {showEmojiPicker && (
                    <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-80 max-h-64 overflow-y-auto z-10">
                      <div className="grid grid-cols-10 gap-1">
                        {EMOJI_LIST.map((emoji, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => insertEmoji(emoji)}
                            className="text-xl hover:bg-gray-100 rounded p-1 transition-colors"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {/* Textarea */}
                <div className="relative">
                  <textarea
                    ref={setTextareaRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={12}
                    placeholder="Schrijf je post..."
                  />
                </div>

                {/* Character Counter with Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className={isOverLimit ? 'text-red-600 font-medium' : 'text-gray-600'}>
                      {content.length} / {characterLimit} tekens
                    </span>
                    {isOverLimit && (
                      <span className="text-red-600 font-semibold">Over limiet!</span>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        isOverLimit
                          ? 'bg-red-500'
                          : characterPercentage > 90
                          ? 'bg-amber-500'
                          : 'bg-blue-500'
                      }`}
                      style={{ width: `${Math.min(characterPercentage, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <SocialImageStrip
                images={images}
                busy={imagesBusy}
                platform={post.platform}
                onAdd={handleAddImages}
                onRemove={handleRemoveImage}
                onReorder={handleReorderImages}
                onError={setError}
              />
            </div>
          </div>

          {/* Preview Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Live preview</h2>

            {/* Platform-specific Preview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Post Header */}
              <div className="p-4 flex items-start gap-3">
                {getPlatformIcon()}
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">Jouw bedrijf</div>
                  <div className="text-sm text-gray-500">Zojuist</div>
                </div>
              </div>

              {/* Post Content */}
              <div className="px-4 pb-4">
                <div className="text-gray-900 whitespace-pre-wrap break-words">
                  {content || (
                    <span className="text-gray-400 italic">
                      Je post tekst verschijnt hier...
                    </span>
                  )}
                </div>
              </div>

              {/* Beelden: Instagram toont één slide in de verhouding van de cover,
                  LinkedIn en Facebook tonen een raster. */}
              {images.length > 0 && (
                <div className="border-t border-gray-200">
                  {post.platform === 'instagram' ? (
                    <div>
                      <div className="w-full bg-gray-100 overflow-hidden" style={{ maxHeight: 500 }}>
                        <img
                          src={images[slide]?.url}
                          alt=""
                          className="w-full h-full object-cover"
                          style={{ aspectRatio: coverRatio, maxHeight: 500 }}
                        />
                      </div>
                      {images.length > 1 && (
                        <div className="flex items-center justify-center gap-1.5 py-2">
                          {images.map((_, i) => (
                            <button
                              key={i}
                              type="button"
                              aria-label={`Beeld ${i + 1}`}
                              onClick={() => setSlide(i)}
                              className={`w-1.5 h-1.5 rounded-full ${
                                i === slide ? 'bg-gray-800' : 'bg-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`grid gap-0.5 ${images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {images.map((image, i) => (
                        <img
                          key={`${image.url}-${i}`}
                          src={image.url}
                          alt=""
                          className="w-full object-cover"
                          style={{ maxHeight: images.length > 1 ? 200 : 500 }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Post Footer (likes, comments, shares mock) */}
              <div className="border-t border-gray-200 px-4 py-3">
                <div className="flex items-center gap-6 text-sm text-gray-500">
                  {post.platform === 'facebook' && (
                    <>
                      <button className="hover:text-blue-600 flex items-center gap-2">
                        <span>👍</span>
                        <span>Vind ik leuk</span>
                      </button>
                      <button className="hover:text-blue-600 flex items-center gap-2">
                        <span>💬</span>
                        <span>Reageren</span>
                      </button>
                      <button className="hover:text-blue-600 flex items-center gap-2">
                        <span>↗️</span>
                        <span>Delen</span>
                      </button>
                    </>
                  )}
                  {post.platform === 'linkedin' && (
                    <>
                      <button className="hover:text-blue-600 flex items-center gap-2">
                        <span>👍</span>
                        <span>Leuk vinden</span>
                      </button>
                      <button className="hover:text-blue-600 flex items-center gap-2">
                        <span>💬</span>
                        <span>Reageren</span>
                      </button>
                      <button className="hover:text-blue-600 flex items-center gap-2">
                        <span>🔄</span>
                        <span>Opnieuw plaatsen</span>
                      </button>
                      <button className="hover:text-blue-600 flex items-center gap-2">
                        <span>📤</span>
                        <span>Verzenden</span>
                      </button>
                    </>
                  )}
                  {post.platform === 'instagram' && (
                    <>
                      <button className="hover:text-red-600 flex items-center gap-2">
                        <span>❤️</span>
                      </button>
                      <button className="hover:text-gray-700 flex items-center gap-2">
                        <span>💬</span>
                      </button>
                      <button className="hover:text-gray-700 flex items-center gap-2">
                        <span>📤</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Info Card */}
            <div className="card bg-blue-50 border-blue-200">
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Preview notitie</p>
                <p className="text-blue-700">
                  Dit is hoe je post er ongeveer uit zal zien op {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}.
                  De exacte weergave kan verschillen per apparaat.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {post && (
        <ScheduleSocialPostModal
          isOpen={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          onConfirm={handleScheduleConfirm}
          platform={post.platform}
          checking={scheduleCheck.checking}
          blockReason={scheduleCheck.blockReason}
          targets={scheduleCheck.targets}
          postTargetId={
            post.platform === 'facebook'
              ? post.facebook_page_id
              : post.platform === 'linkedin'
                ? post.linkedin_page_id
                : null
          }
          onGoToSettings={() => {
            setScheduleModalOpen(false);
            setScheduleCheck({ checking: false, blockReason: null, targets: null });
            navigate('/dashboard/settings?tab=socials');
          }}
        />
      )}
    </div>
  );
}
